// BWC Care Plans — the recurring, universal maintenance offer. Renamed
// from "Growth Plans" (was Essentials / Growth Partner / Growth Engine)
// per the pricing restructure: this section is now always shown, priced
// identically, to every visitor regardless of whether they paid for a
// build or got one free — see Pricing.jsx's build-type toggle, which this
// section sits below and is completely unaffected by.
//
// These are deliberately namespaced with a `gp-` id prefix so they never
// collide with the one-time website tiers in `content.js` (`launch`,
// `growth`, `scale`). InquiryForm and GrowthPlans both rely on that prefix
// to tell "which website package did they pick" apart from "which care
// plan did they pick" — the two selections are independent and a client
// can express interest in both (e.g. Growth website + Growth Engine plan).
//
// Extending this later (ads, branding, AI automation, funnels) means
// adding a `features` line or a new plan object here — the section and
// the inquiry form don't need to change shape to support it.
//
// `priceMonthly` (a raw Naira number) + `annualDiscountPercent` drive the
// monthly/annual toggle in GrowthPlans.jsx — the toggle computes the annual
// rate itself rather than storing two separate hardcoded strings, so the
// numbers can't drift out of sync with each other. Plans without a numeric
// price (Growth Engine's custom quote) just omit `priceMonthly`, and the
// toggle leaves their display string untouched.
const NGN_PER_USD = 1380 // approx, July 2026 — update periodically, rate moves

export const growthPlans = [
  {
    id: 'gp-essentials',
    name: 'Website Care',
    for: 'Keep your website accurate, secure, and working.',
    priceMonthly: 15000,
    annualDiscountPercent: 15,
    description:
      "The baseline every live website needs: small updates handled promptly, nothing broken, nothing stale, while you focus on running the business.",
    features: [
      'Small text, price and photo updates',
      'Minor content changes',
      'Fixing minor website issues and keeping core functionality working',
      'Basic SEO upkeep, including page titles, descriptions and relevant keywords',
      'Adding one blog article periodically',
    ],
    // Rendered as a distinct, muted note below the feature list rather
    // than a bullet — it's a boundary on the plan, not a feature of it.
    note: 'Major changes, new pages, redesigns, new functionality and substantial content work are not included and would be quoted separately.',
    cta: 'Start with Website Care',
    featured: false,
  },
  {
    id: 'gp-growth-partner',
    name: 'Care + Strategy',
    badge: 'Recommended',
    for: 'For businesses ready to actively grow their enquiries, not just stay online.',
    priceMonthly: 45000,
    annualDiscountPercent: 15,
    description:
      'Everything in Website Care, plus a strategist actively working on your site and your content every month, so both keep getting better at turning attention into customers.',
    features: [
      'Everything in Website Care',
      'Keyword & search opportunity research',
      'Conversion optimisation pass',
      'Monthly analytics & performance review',
      'Priority support',
      "A monthly review of your existing social content, with practical feedback on what's working, what to do more/less of, and a few content/video ideas for the month ahead (advisory only — we don't create, edit, schedule or post your social content)",
    ],
    cta: 'Become a Care + Strategy Partner',
    featured: true,
  },
  {
    id: 'gp-growth-engine',
    name: 'Growth Engine',
    for: 'A full growth partnership for businesses scaling fast.',
    price: 'Custom Partnership',
    description:
      "For businesses that need more than a monthly checklist: a dedicated growth partnership built around specific revenue goals.",
    features: [
      'Everything in Care + Strategy',
      'Dedicated landing pages for campaigns',
      'AI-powered automations',
      'Advanced conversion rate optimisation',
      'Monthly strategy session',
      'Direct line to your strategist',
    ],
    cta: "Let's Talk Growth",
    featured: false,
  },
]

// Formats a plan's price for a given billing cycle. Plans with no
// `priceMonthly` (Growth Engine) just return their static `price` string
// regardless of cycle.
export function formatPlanPrice(plan, cycle) {
  if (!plan.priceMonthly) {
    return { price: plan.price, usdPrice: null, savingsLabel: null }
  }
  if (cycle === 'annual') {
    const discount = plan.annualDiscountPercent || 0
    const annualTotal = Math.round((plan.priceMonthly * 12 * (100 - discount)) / 100)
    const effectiveMonthly = Math.round(annualTotal / 12)
    return {
      price: `From $${Math.round(effectiveMonthly / NGN_PER_USD)}/mo`,
      usdPrice: `~₦${effectiveMonthly.toLocaleString('en-NG')}/mo`,
      billedNote: `Billed ~₦${annualTotal.toLocaleString('en-NG')}/yr`,
      savingsLabel: discount ? `Save ${discount}%` : null,
    }
  }
  return {
    price: `From $${Math.round(plan.priceMonthly / NGN_PER_USD)}/mo`,
    usdPrice: `~₦${plan.priceMonthly.toLocaleString('en-NG')}/mo`,
    billedNote: null,
    savingsLabel: null,
  }
}

// A small, curated comparison matrix — not a mechanical dump of each plan's
// full feature list (those use different wording per plan on purpose, to
// read as prose rather than a spec sheet). This is deliberately short:
// enough to answer "what do I actually get more of at the next tier" at a
// glance, not a full audit-proof feature-parity table.
export const growthPlanComparison = [
  { label: 'Text, price & photo updates', values: [true, true, true] },
  { label: 'Minor fixes & core functionality upkeep', values: [true, true, true] },
  { label: 'Basic SEO & periodic blog article', values: [true, true, true] },
  { label: 'Keyword & search opportunity research', values: [false, true, true] },
  { label: 'Conversion optimisation', values: [false, true, true] },
  { label: 'Monthly analytics & performance review', values: [false, true, true] },
  { label: 'Monthly social content review', values: [false, true, true] },
  { label: 'Dedicated campaign landing pages', values: [false, false, true] },
  { label: 'AI-powered automations', values: [false, false, true] },
  { label: 'Monthly strategy session', values: [false, false, true] },
  { label: 'Support level', values: ['Standard', 'Priority', 'Direct line'] },
]
